import { useEffect, useRef } from "react";
import { compareVersions } from "../logic/logics_common";
import { sendMessage } from "utils/webview";
import { v4 as uuidv4 } from "uuid";

type Params = {
  user: any;
  isInappWebview?: boolean;
  appVersion: string;
  setMixPanel: () => void;
};

/**
 * 결제 페이지 진입 시 공통 트래킹 훅
 * 순서: Meta CAPI(>=1.1.99) → FB Fallback(>=1.1.17) → Firebase/Appsflyer(>=1.1.47) → Mixpanel
 * 가드: suppress_payment_page_view(localStorage) + sentEventRef(중복 방지)
 */
export default function usePaymentPageViewTracking({
  user,
  isInappWebview,
  appVersion,
  setMixPanel,
}: Params) {
  const sentEventRef = useRef(false);

  // 결제 성공 후 재진입 시 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송 차단 플래그
  const initialSuppress = (() => {
    try {
      return localStorage.getItem("suppress_payment_page_view") === "1";
    } catch (e) {
      return false;
    }
  })();
  const suppressPageViewRef = useRef<boolean>(initialSuppress);

  // suppress 플래그는 1회성으로 사용 후 제거
  useEffect(() => {
    if (suppressPageViewRef.current) {
      try {
        localStorage.removeItem("suppress_payment_page_view");
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (!sentEventRef.current && !suppressPageViewRef.current) {
      // Meta CAPI (>= 1.1.99) or FB fallback (>= 1.1.17)
      if (
        user?.isInsider !== true &&
        isInappWebview &&
        compareVersions(appVersion, "1.1.99") >= 0
      ) {
        if (user?.segmentType) {
          sendMessage(
            JSON.stringify({
              event: "send_meta_capi_event",
              params: {
                eventId: uuidv4(),
                eventName: `page_view_payment_type_${user?.segmentType}`,
                eventParams: {
                  content_ids: [`distancing_program_type_${user?.segmentType}`],
                  content_type: "product",
                  content_name: `디스턴싱 프로그램 Type ${user?.segmentType}`,
                  content_category: "Digital Program",
                  value: user?.segmentType === "a" ? 24000 : 0,
                  currency: "KRW",
                },
              },
            })
          );
        }
        sendMessage(
          JSON.stringify({
            event: "send_meta_capi_event",
            params: {
              eventId: uuidv4(),
              eventName: "page_view_payment",
              eventParams: {
                content_ids: ["distancing_program_main"],
                content_type: "product",
                content_name: "디스턴싱 프로그램",
                content_category: "Digital Program",
                value: 16000,
                currency: "KRW",
              },
            },
          })
        );
      } else if (
        user?.isInsider !== true &&
        isInappWebview &&
        compareVersions(appVersion, "1.1.17") >= 0
      ) {
        if (user?.segmentType) {
          sendMessage(
            JSON.stringify({
              event: "send_fb_event",
              params: {
                eventName: `page_view_payment_type_${user?.segmentType}`,
              },
            })
          );
        }
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "page_view_payment",
            },
          })
        );
      }

      // Firebase, Appsflyer (>= 1.1.47)
      if (
        user?.isInsider !== true &&
        isInappWebview &&
        compareVersions(appVersion, "1.1.47") >= 0
      ) {
        sendMessage(
          JSON.stringify({
            event: "send_appsflyer_event",
            params: {
              eventName: "af_content_view",
              eventParams: {
                af_content_id: "distancing_program",
              },
            },
          })
        );

        if (user?.segmentType === "a") {
          sendMessage(
            JSON.stringify({
              event: "send_appsflyer_event",
              params: {
                eventName: "af_content_view_type_a",
                eventParams: {
                  af_content_id: "distancing_program_typa_a",
                },
              },
            })
          );

          sendMessage(
            JSON.stringify({
              event: "send_firebase_analytics_event",
              params: {
                eventName: "screen_view",
                eventParams: {
                  screen_name: "PaymentScreen",
                  screen_class: "PaymentScreenComponent",
                },
              },
            })
          );
        }
      }

      // Mixpanel
      setMixPanel();

      sentEventRef.current = true;
    }
  }, [user?.isInsider, user?.segmentType, isInappWebview, appVersion]);

  return { sentEventRef, suppressPageViewRef } as const;
}
